import React, { useState, useEffect } from "react";
import type { AutoCreateAlbumOptions } from "../types/AutoCreateAlbumOptions";
import type { PageType } from "../utils/albumData";
import type { AlbumImage } from "../types/AlbumSaveData";
import "./AutoCreateAlbumDialog.css";

interface AutoCreateDialogProps {
    open: boolean;
    totalImages: number;
    totalPages: number;
    albumType: string;
    albumName: string;
    token: string | null;
    pages: PageType[];
    stageSize: { width: number; height: number };
    updatePages?: (fn: (prev: PageType[]) => PageType[]) => void;
    setMarkedImages?: (fn: (prev: AlbumImage[]) => AlbumImage[]) => void;
    onConfirm?: (options: AutoCreateAlbumOptions) => void;
    onClose: () => void;
}
// In your dialog component
type ImagesPerSpread = "auto" | "fixed"; // for radio buttons

export default function AutoCreateAlbumDialog({
    open,
    totalImages,
    totalPages,
    onConfirm,
    onClose,
}: AutoCreateDialogProps) {
    const [imagesPerPage, setImagesPerPage] = useState<ImagesPerSpread>("auto");
    const [fixedCount, setFixedCount] = useState<1 | 2 | 3 | 4 | 5 | 6 | 7>(7);
    const [order, setOrder] = useState<"import" | "random">("import");
    const [usage, setUsage] = useState<"fill" | "all">("fill");
    const MIN_IMAGES_PER_SPREAD = 1;
    const minRequiredImages = totalPages * MIN_IMAGES_PER_SPREAD;
    const underflow = totalImages < minRequiredImages;
    const maxCapacity = totalPages * 7;
    const overflow = totalImages > maxCapacity;
    const allDisabled = imagesPerPage === "fixed" || overflow;


    {/* Automatically switch usage to "fill" if overflow */ }
    useEffect(() => {
        if (overflow && usage === "all") {
            setUsage("fill");
        }
    }, [overflow, usage]);


    if (!open) return null;

    return (
        <div className="modal-backdrop">
            <div className="modal">
                <header className="modal-header">
                    <span>Auto Fill Spreads</span>
                </header>

                {/* Images per page */}
                <section>
                    <h3>Images per page</h3>

                    {/* Auto option */}
                    <label>
                        <input
                            type="radio"
                            checked={imagesPerPage === "auto"}
                            onChange={() => setImagesPerPage("auto")}
                        />
                        Auto (recommented)
                    </label>

                    {/* Fixed option */}
                    <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <input
                            type="radio"
                            checked={imagesPerPage === "fixed"}
                            onChange={() => setImagesPerPage("fixed")}
                        />
                        Fixed
                        {imagesPerPage === "fixed" && (
                            <select
                                className="form-select"
                                value={fixedCount}
                                onChange={(e) =>
                                    setFixedCount(Number(e.target.value) as 1 | 2 | 3 | 4 | 5 | 6 | 7)
                                }
                                style={{ height: "28px", padding: "0 8px" }} // match your other dropdowns
                            >
                                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                                    <option key={n} value={n}>
                                        {n} image{n > 1 ? "s" : ""}
                                    </option>
                                ))}
                            </select>
                        )}
                    </label>
                </section>

                {/* Order */}
                <section>
                    <h3>Image order</h3>
                    <label>
                        <input
                            type="radio"
                            checked={order === "import"}
                            onChange={() => setOrder("import")}
                        /> File Name
                    </label>
                    <label>
                        <input
                            type="radio"
                            checked={order === "random"}
                            onChange={() => setOrder("random")}
                        /> Randomize
                    </label>
                </section>

                {/* Usage */}
                <section>
                    <h3>Image usage</h3>
                    <label>
                        <input
                            type="radio"
                            checked={usage === "fill"}
                            onChange={() => setUsage("fill")}
                        /> Fill album capacity
                    </label>
                    <label>
                        <input
                            type="radio"
                            checked={usage === "all"}
                            onChange={() => !allDisabled && setUsage("all")}
                            disabled={allDisabled}
                            title={allDisabled ? "Cannot use all images when fixed count is selected" : undefined}
                        /> Use all images
                    </label>
                </section>

                {/* Overflow warning */}
                {overflow && usage === "fill" && (
                    <p className="warning">
                        ⚠️ {totalImages} images detected, but the album can hold only {maxCapacity}.
                        Extra images will be ignored.
                    </p>
                )}
                {/* Underflow warning */}
                {underflow && (
                    <p className="warning">
                        ⚠️ Not enough images to create this album.<br />
                        Minimum required: {minRequiredImages} images ({MIN_IMAGES_PER_SPREAD} per spread).<br />
                        You currently have {totalImages}.
                    </p>
                )}
                {underflow && (
                    <p className="hint">
                        Tip: Add more images or reduce the number of pages.
                    </p>
                )}

                {/* Actions */}
                <footer>
                    <button onClick={onClose}>Cancel</button>
                    <button
                        disabled={underflow}
                        title={underflow ? `At least ${minRequiredImages} images are required` : undefined}
                        onClick={() => {
                            if (underflow) return;
                            onConfirm?.({
                                imagesPerPage: imagesPerPage === "auto" ? "auto" : fixedCount,
                                order,
                                usage,
                            });
                            onClose();
                        }}
                    >
                        Create Album
                    </button>
                </footer>
            </div>
        </div>
    );
}